/**
 * 会话上下文：管理员摘要 + 登录/登出动作（过期跳转在 AuthGuard 中处理）。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AdminSummary, LoginResponse } from '@chronospeak/contracts';
import { adminHttp, setAccessToken, setSessionExpiredHandler } from '../lib/api';

interface SessionState {
  admin: AdminSummary | null;
  /** null = 加载中（守卫期间不闪烁登录页） */
  ready: boolean;
  login: (email: string, password: string) => Promise<AdminSummary>;
  logout: () => Promise<void>;
  refreshAdmin: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminSummary | null>(null);
  const [ready, setReady] = useState(false);

  const refreshAdmin = useCallback(async () => {
    try {
      const me = await adminHttp.get<AdminSummary>('/admin/me');
      setAdmin(me);
    } catch {
      setAdmin(null);
      setAccessToken(null);
    }
  }, []);

  // 挂载时尝试用既有 refresh Cookie 静默恢复会话
  useEffect(() => {
    (async () => {
      try {
        const res = await adminHttp.post<{ accessToken: string }>('/admin/auth/refresh');
        setAccessToken(res.accessToken);
        await refreshAdmin();
      } catch {
        setAdmin(null);
      } finally {
        setReady(true);
      }
    })();
  }, [refreshAdmin]);

  // 会话过期统一跳转（401 且 refresh 失败时由 api.ts 触发）
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setAdmin(null);
      setAccessToken(null);
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<AdminSummary> => {
      const res = await adminHttp.post<LoginResponse>('/admin/auth/login', {
        email,
        password,
      });
      setAccessToken(res.accessToken);
      setAdmin(res.admin);
      return res.admin;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await adminHttp.post('/admin/auth/logout');
    } finally {
      setAccessToken(null);
      setAdmin(null);
    }
  }, []);

  const value = useMemo(
    () => ({ admin, ready, login, logout, refreshAdmin }),
    [admin, ready, login, logout, refreshAdmin],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession 必须在 SessionProvider 内使用');
  return ctx;
}
